import { type ObjectId } from 'mongodb';
import { Artifact } from '~api/model/artifact.model';
import { ToolOutputs } from '~api/types/tools.types';
import { Obj, Replace, Vals } from '~lib';
import { ClaudeMessage, MaybeDocId } from '../types';
import { AgentMode } from './agent.model';

export namespace Conversation {
  /**
   * @desc Longest title accepted by `conversations.rename`. Generated summaries aim for ~64
   * characters, so this leaves headroom for a manual title without letting the sidebar row
   * become an essay.
   */
  export const TitleMaxLength = 120;

  export const AnswerPath = {
    Slotted: 'slotted',
    Unslotted: 'unslotted',
    /**
     * @desc A reply produced without consulting the data at all (greeting, clarifying question).
     * Separated from `unslotted` so drift ratios are not diluted by conversational style.
     */
    Conversational: 'conversational',
    Abstained: 'abstained',
  } as const;
  export type AnswerPath = Vals<typeof AnswerPath>;

  export type MessageMetadata = {
    turnId: string;
    turn?: {
      tStart: string; // iso
      tFinish?: string; // iso
      tElapsed?: number; // ms
    };
    modelCall?: {
      tStart: string; // iso
      tFirstChunk?: number; // ms — first *text* chunk; the portal renders this as reasoning duration
      tFirstToken?: number; // ms — first streamed output of any kind (thinking or text)
      tLastChunk?: number; // ms
    };
    modelConfig?: {
      provider: string;
      model: string;
      /**
       * @desc Sampling temperature the request carried. Present only on documents written before
       * the backend stopped sending `temperature` (deprecated by the API for every model after
       * Claude Opus 4.6; see `ai-client.ts`). New metadata omits it — read absent as "API default".
       */
      temperature?: number;
      systemPromptHash: string;
    };
    answer?: {
      path?: AnswerPath;
      verification?: 'rejected';
    };
    /**
     * @desc Token usage the provider reported for this model call, split the way the quota's
     * credit weights need it (see `lib/quota.ts`).
     *
     * Persisted with the conversation, and therefore the **repair source for the usage ledger**:
     * a ClickHouse append is fire-and-forget and never retried, so when one is lost this is the
     * only place the facts survive. A backfill reads these and re-inserts keyed on the event id,
     * which is the message id this metadata hangs off.
     *
     * Declared structurally rather than importing the backend's `TokenUsage` — `internal/api` must
     * not depend on `apps/backend`, the same reason `MeteredIdentity` is spelled out in
     * `lib/auth.ts`.
     */
    usage?: {
      input: number;
      output: number;
      cacheRead: number;
      cacheWrite5m: number;
      cacheWrite1h: number;
    };
  };

  /**
   * @desc The true source of the event, as opposed to `message.role: 'user' | 'assistant'`
   *  used in the Anthropic API interactions.
   */
  export type EventSource =
    | 'user' // from user input
    | 'agent' // generated by agent orchestrator or tooling
    | 'model'; // output from the language model

  type TEvent<
    TDate extends string | Date,
    Source extends EventSource,
    Name extends string,
    Content,
    Metadata extends Obj = Obj,
  > = {
    /**
     * @desc Unique event identity, assigned at creation (`newEventId()`, or the upstream
     * Anthropic message id for model messages). The idempotent append guard
     * (`conversations.store.ts pushState`) and client-side replay dedupe key on it.
     * Events persisted before identity existed may lack it at runtime — readers must
     * tolerate its absence on legacy documents even though new events always carry it.
     */
    id: string;
    name: Name;
    source: Source;
    payload: Content;
    timestamp: TDate;
    metadata: Metadata;
  };

  // these events form the context interpreted by the language model
  export type ClaudeMessageEvent<TDate extends string | Date = Date> = TEvent<
    TDate,
    EventSource,
    'ClaudeMessage',
    ClaudeMessage.All,
    MessageMetadata
  >;

  type TF<Format extends Artifact.Format> = Extract<ToolOutputs.OutputItem, { format: Format }>;

  type ToolOutputItem<Format extends Artifact.Format, OId extends string | ObjectId = ObjectId> = {
    format: Format;
    summary: TF<Format>['summary'];
  } & (
    | { data: string; artifactId: null }
    | { data: null; artifactId: OId; artifact?: ToolOutputs.ArtifactItemMeta<OId> }
  );

  export type AllToolOutputItems<OId extends string | ObjectId = ObjectId> = Vals<{
    [F in Artifact.Format]: ToolOutputItem<F, OId>;
  }>;

  type ToolOutputEventPayload<OId extends string | ObjectId = ObjectId> = {
    toolUseId: string;
    toolName: string;
    error: null | string;
    trace: null | string;
    items: Array<AllToolOutputItems<OId>>;
  };

  type ToolOutputMetadata = MessageMetadata & {
    callMessageId?: string;
    tool?: {
      tStart: string; // iso
      tFinish: string; // iso
      tElapsed: number; // ms
      failureKind?: 'clickhouse_timeout';
    };
  };

  export type ToolOutputEvent<
    TDate extends string | Date = Date,
    OId extends string | ObjectId = ObjectId,
  > = TEvent<TDate, 'agent', 'ToolOutput', ToolOutputEventPayload<OId>, ToolOutputMetadata>;

  export type SystemEventPayloads =
    | {
        code: 'ToolsFinished';
        status: 'completed';
        failures: null;
      }
    | {
        code: 'ToolsFinished';
        status: 'failed';
        failures: string | Array<{ tool: string; error: string; trace: string }>;
      }
    | {
        code: 'ModelCallFailed';
        retryable: boolean;
        status?: number;
        error: string | Obj;
        /**
         * @desc Why the call failed, where the harness needs to tell the user something other
         * than "a model call failed". `output_truncated` is a reply cut off by the output-token
         * ceiling (`stop_reason: 'max_tokens'`): a real, charged model call whose content is
         * unusable — never an answer, and never a dispatchable tool call. Absent on an ordinary
         * provider/transport failure, whose `error` is internal detail and is never shown.
         */
        reason?: 'output_truncated';
      }
    | {
        code: 'Summarisation';
        summary: string | null;
        comment?: string;
      }
    | {
        code: 'ThinkingStart';
      }
    | {
        code: 'WarmingUp';
      }
    | {
        code: 'TurnComplete';
      }
    | {
        /**
         * @desc A soft nudge (H2-152): this conversation's context has grown past the configured
         * threshold and the user is invited to start a fresh one. Advice only — nothing is
         * refused, ended or bounded by it, which is what separates it from every other event on
         * this union. The absolute turn cap and token budget remain open under H2-158.
         */
        code: 'ConversationLengthAdvisory';
        /**
         * @desc Prompt tokens the largest model call in this conversation read — uncached input
         * plus cache reads plus cache writes. NOT a sum over calls: with prompt caching a turn's
         * ~15 calls each re-read the whole context, so a sum reports roughly fifteen times the
         * conversation's actual length. See `agent/context-advisory.ts`.
         */
        contextTokens: number;
        /** @desc The threshold that was passed, carried so the record explains its own trigger. */
        thresholdTokens: number;
      };

  type SystemEventMetadata = MessageMetadata & {
    callMessageId?: string;
    /**
     * @desc On a `ToolsFinished` event, the tools whose consecutive-failure count reached the
     * turn-ending threshold. The payload's `failures` lists everything that failed in the batch,
     * which is a different question — a tool can fail without exhausting its repair budget.
     */
    toolBreak?: { tools: string[] };
  };

  export type SystemEvent<
    TDate extends string | Date = Date,
    Payload = SystemEventPayloads,
  > = TEvent<TDate, 'agent', 'System', Payload, SystemEventMetadata>;

  export type AllSystemEventCodes = SystemEventPayloads['code'];

  export type SystemEventByCode<
    Code extends AllSystemEventCodes,
    TDate extends string | Date = Date,
  > = SystemEvent<TDate, Extract<SystemEventPayloads, { code: Code }>>;

  export const isSystemEventByCode =
    <Code extends AllSystemEventCodes, D extends string | Date, OId extends string | ObjectId>(
      code: Code,
    ) =>
    (evt: HistoryEvent<D, OId>): evt is SystemEventByCode<Code, D> =>
      evt.name === 'System' && evt.payload.code === code;

  export type HistoryEvent<
    TDate extends string | Date = Date,
    OId extends string | ObjectId = ObjectId,
  > = ClaudeMessageEvent<TDate> | ToolOutputEvent<TDate, OId> | SystemEvent<TDate>;

  export const isHistoryEventByName =
    <
      TDate extends string | Date = Date,
      OId extends string | ObjectId = ObjectId,
      Name extends HistoryEvent['name'] = HistoryEvent['name'],
    >(
      name: Name,
    ) =>
    (e: HistoryEvent<TDate, OId>): e is Extract<HistoryEvent<TDate, OId>, { name: Name }> =>
      e.name === name;

  export const evtToSerial = (e: HistoryEvent): HistoryEvent<Date, string> => {
    // Handle ToolOutputEvent with potential ObjectId artifactIds
    if (e.name === 'ToolOutput') {
      return {
        ...e,
        payload: {
          ...e.payload,
          items: e.payload.items.map((item) => {
            if ('artifactId' in item && !!item.artifactId) {
              const { artifact, ...rest } = item;
              return {
                ...rest,
                data: null,
                artifactId: item.artifactId.toString(),
                ...(artifact
                  ? {
                      artifact: {
                        ...artifact,
                        lineageId: artifact.lineageId.toString(),
                        supersedes: artifact.supersedes ? artifact.supersedes.toString() : null,
                        derivedFrom: artifact.derivedFrom ? artifact.derivedFrom.toString() : null,
                      },
                    }
                  : {}),
              };
            }
            return item;
          }),
        },
      } satisfies ToolOutputEvent<Date, string>;
    }

    return e satisfies HistoryEvent<Date, string>;
  };

  export namespace ClientEvents {
    export type User<TDate extends string | Date = Date> = ClaudeMessageEvent<TDate>;
    export type Model<TDate extends string | Date = Date> = ClaudeMessageEvent<TDate>;
    export type Agent<
      TDate extends string | Date = Date,
      OId extends string | ObjectId = ObjectId,
    > = SystemEvent<TDate> | ToolOutputEvent<TDate, OId>;
  }

  export type ClientEvent<
    TDate extends string | Date = Date,
    OId extends string | ObjectId = string,
  > = ClientEvents.User<TDate> | ClientEvents.Model<TDate> | ClientEvents.Agent<TDate, OId>;

  /**
   * @property history The complete log of agent events.
   * @property contexts Separate projections of the conversation for interactions with
   *  the language model and UI respectively.
   * @todo implement chained contexts next
   */
  export type Model<
    OId extends string | ObjectId = ObjectId,
    TDate extends string | Date = Date,
    TDocId extends void | string | ObjectId = OId,
  > = MaybeDocId<TDocId> & {
    refs: {
      userId: string;
    };
    /** @desc Agent persona, fixed at creation. Absent on legacy docs (treat as default). */
    mode?: AgentMode.T;
    summary: null | string;
    /**
     * @desc Set when the owner renames the conversation from the sidebar. The title is then
     * theirs: the agent skips summary generation entirely rather than generating one and
     * discarding it. Absent on legacy docs (treat as unlocked).
     */
    summaryLocked?: boolean;
    metadata?: {
      summaries?: Array<{
        text: string;
        createdAt: TDate;
        reason: string;
      }>;
    };
    /**
     * @desc Lease on the currently executing turn (H2-172): claimed atomically when a prompt
     * is accepted, released at the turn's terminal state. The cross-process source of truth
     * for "a run is in flight" — the API's CONFLICT guard and the broker's
     * `conversation:watch` answer both read it, so it must never be trusted past its lease
     * window (a crashed process cannot release; see the store's claim/expiry logic).
     * Absent on legacy docs and between turns.
     */
    activeTurn?: null | {
      turnId: string;
      startedAt: TDate;
    };
    history: Array<HistoryEvent<TDate, OId>>;
    contexts: {
      assistant: Array<ClaudeMessageEvent<TDate>['payload']>;
      user: Array<ClientEvent<TDate, OId>>;
    };
    deleted?: boolean;
    deletedAt?: TDate;
    createdAt: TDate;
    updatedAt: TDate;
  };

  /**
   * @desc The event-log portion of a conversation: the append unit flowing from the agent
   * pipeline into the persistence layer. Lives in the shared model (not `~backend/agent`)
   * so stores — and, in later H2-172 phases, the broker — depend only on model code.
   */
  export type AgentState = Pick<Model, 'history' | 'contexts' | 'summary' | 'summaryLocked'>;

  export const newAgentState = (): AgentState => ({
    history: [],
    contexts: { assistant: [], user: [] },
    summary: null,
    summaryLocked: false,
  });

  /** @desc Mint a conversation event id. Centralised in the shared model so every event producer — backend today, the broker in a later H2-172 phase — mints compatible identities. */
  export const newEventId = (): string => crypto.randomUUID();

  /**
   * @desc Build the user's prompt event.
   *
   * Two tiers produce this exact event and must produce it identically: the API persists it the
   * moment a turn is accepted (H2-172 — so a new or reloaded chat is never blank while the run
   * side spins up), and the agent emits it when it runs a turn whose prompt was not persisted
   * ahead of it. The envelope lives here rather than in either tier so the two cannot drift.
   *
   * `metadata.turnId` is the caller's, not minted here: every event in a turn groups under one
   * id on the client (`collectEventGroups`), so the id the API stamps has to be the same one the
   * run stamps on everything that follows — the accepted turn's id.
   */
  export const userMessageEvent = ({
    id,
    content,
    metadata,
    timestamp = new Date(),
  }: {
    id: string;
    content: ClaudeMessage.Input['content'];
    metadata: MessageMetadata;
    timestamp?: Date;
  }): ClaudeMessageEvent => ({
    id,
    name: 'ClaudeMessage',
    source: 'user',
    payload: {
      role: 'user',
      content: typeof content === 'string' ? [{ type: 'text', text: content }] : content,
    },
    timestamp,
    metadata,
  });

  /**
   * @desc The single-event append unit for a user prompt: exactly the projections the reducer
   * derives for a `source: 'user'` ClaudeMessage, so a prompt persisted by the API is
   * indistinguishable from one persisted by the run.
   */
  export const userMessageState = (evt: ClaudeMessageEvent): AgentState => ({
    ...newAgentState(),
    history: [evt],
    contexts: { assistant: [evt.payload], user: [evt] },
  });

  export const toSerial = (
    c: Model,
  ): Replace<
    Omit<Model<string, Date, string>, 'contexts'>,
    { history: Model<string, Date, string>['contexts']['user']; mode: AgentMode.T }
  > => ({
    _id: c._id.toString(),
    refs: c.refs,
    mode: AgentMode.coerce(c.mode),
    summary: c.summary,
    // In-flight turn lease, surfaced so a loading client knows to expect live events without
    // an extra roundtrip. Lease-expiry interpretation stays server-side; treat as a hint.
    activeTurn: c.activeTurn ?? null,
    history: c.contexts.user.map(evtToSerial),
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  });

  export type Serial = ReturnType<typeof toSerial>;

  /**
   * @desc Longest first-prompt text an inspector listing row carries, in Unicode code points.
   * Truncation happens in the Mongo pipeline (`$substrCP`), so a list never ships a whole prompt:
   * enough for a one-line row plus a tooltip, and at most ~60k characters for a 200-row page.
   */
  export const FirstPromptMaxLength = 300;

  /**
   * @desc The conversation's first user prompt, as an inspector listing row shows it.
   *
   * - `text` — the prompt's text blocks joined with newlines and trimmed, then cut to
   *   {@link FirstPromptMaxLength} code points. A legacy prompt stored as a bare string (the
   *   agent wrote them unwrapped before October 2025) is read the same way. Empty when the
   *   prompt carried no text block at all.
   * - `truncated` — the full text was longer than `text`.
   * - `at` — the prompt event's timestamp.
   */
  export type FirstPrompt = { text: string; truncated: boolean; at: Date };

  /**
   * @desc What ended a {@link SessionSpan}: the latest finished turn, or — when no turn has
   * finished — the last event the log recorded.
   */
  export const SessionEnd = {
    FinishedTurn: 'finished-turn',
    LastEvent: 'last-event',
  } as const;
  export type SessionEnd = Vals<typeof SessionEnd>;

  /**
   * @desc Wall-clock span of a conversation: first prompt to last answer. Distinct from turn
   * time (`InspectorStats.totalTurnElapsedMs`), which sums only the time the agent was working
   * and leaves out the user's think time between turns — never derive one from the other.
   *
   * - `startedAt` — the first `source: 'user'` ClaudeMessage's timestamp, in log order.
   * - `endedAt` — the largest `metadata.turn.tFinish` in the log (`endedBy: 'finished-turn'`).
   *   Errored turns count: a model failure, a truncation and an abstention all stamp `tFinish`.
   * - **In progress / died:** when no turn has finished at all — the first turn is still running,
   *   or its process died before stamping `tFinish` — `endedAt` falls back to the last event's
   *   timestamp (`endedBy: 'last-event'`). That is a lower bound and `endedBy` says so, which is
   *   more useful to an admin than a blank: a conversation that only ever ran one broken turn is
   *   exactly the one worth looking at. A later turn still running after an earlier one finished
   *   does not extend the span — it has not produced an answer yet.
   * - `durationMs` — `endedAt - startedAt`, clamped at zero.
   *
   * The whole span is null when the log has no user prompt to start from.
   */
  export type SessionSpan = {
    startedAt: Date;
    endedAt: Date;
    endedBy: SessionEnd;
    durationMs: number;
  };

  /**
   * @desc The {@link SessionSpan} rule, over the three facts it needs. One definition shared by
   * the listing (fed from the Mongo digest) and the inspector conversation view (fed from the
   * history), so the two can never disagree about what a session's length means.
   */
  export const sessionSpan = ({
    firstPromptAt,
    lastTurnFinishAt,
    lastEventAt,
  }: {
    firstPromptAt: Date | null;
    lastTurnFinishAt: Date | null;
    lastEventAt: Date | null;
  }): SessionSpan | null => {
    if (!firstPromptAt) return null;
    const [endedAt, endedBy] = lastTurnFinishAt
      ? [lastTurnFinishAt, SessionEnd.FinishedTurn]
      : [lastEventAt ?? firstPromptAt, SessionEnd.LastEvent];
    return {
      startedAt: firstPromptAt,
      endedAt,
      endedBy,
      durationMs: Math.max(0, endedAt.getTime() - firstPromptAt.getTime()),
    };
  };

  /** @desc {@link sessionSpan} read straight off an event log. */
  export const sessionSpanOf = <OId extends string | ObjectId>(
    history: ReadonlyArray<HistoryEvent<Date, OId>>,
  ): SessionSpan | null => {
    let firstPromptAt: Date | null = null;
    let lastTurnFinishAt: Date | null = null;
    let lastEventAt: Date | null = null;
    for (const e of history) {
      if (!firstPromptAt && e.name === 'ClaudeMessage' && e.source === 'user')
        firstPromptAt = e.timestamp;
      if (!lastEventAt || e.timestamp > lastEventAt) lastEventAt = e.timestamp;
      const tFinish = e.metadata.turn?.tFinish;
      if (!tFinish) continue;
      const finishedAt = new Date(tFinish);
      if (!lastTurnFinishAt || finishedAt > lastTurnFinishAt) lastTurnFinishAt = finishedAt;
    }
    return sessionSpan({ firstPromptAt, lastTurnFinishAt, lastEventAt });
  };

  /**
   * @desc Admin-only projection for the conversation inspector (H2-172 add-admin-conversation-inspector):
   * unlike `toSerial`, which projects `contexts.user` (a chat-UI-safe view that drops
   * `source: 'agent'` ClaudeMessages — withheld drafts, repair prompts), this surfaces the
   * complete `history` event log unfiltered. `contexts` stays omitted: the inspector reads the
   * raw history, not the assistant-facing projection, which the portal chat UI must never
   * receive anyway. `session` is the wall-clock {@link SessionSpan}, computed here so the
   * conversation view's header reads the same rule as the listing row.
   */
  export const toInspectorSerial = (
    c: Model,
  ): Replace<
    Omit<Model<string, Date, string>, 'contexts'>,
    { history: Array<HistoryEvent<Date, string>>; mode: AgentMode.T }
  > & { session: SessionSpan | null } => ({
    _id: c._id.toString(),
    refs: c.refs,
    mode: AgentMode.coerce(c.mode),
    summary: c.summary,
    activeTurn: c.activeTurn ?? null,
    history: c.history.map(evtToSerial),
    session: sessionSpanOf(c.history),
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  });

  export type InspectorSerial = ReturnType<typeof toInspectorSerial>;

  /**
   * @desc Viewer-relative access to a conversation, decided by the backend per
   * request. `owner` may read and mutate; `readonly` (an admin viewing another
   * user's conversation) may read only. The client trusts this field rather
   * than re-deriving ownership, so gating logic lives in one place.
   */
  export const Access = {
    Owner: 'owner',
    ReadOnly: 'readonly',
  } as const;
  export type Access = Vals<typeof Access>;

  /** @desc A serialised conversation stamped with the requesting viewer's access. */
  export type SerialView = Serial & { access: Access };

  // prepare as metadata-only list items
  export const toListing = (c: Model) => ({
    _id: c._id.toString(),
    refs: c.refs,
    mode: AgentMode.coerce(c.mode),
    summary: c.summary,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  });

  export type Listing = ReturnType<typeof toListing>;

  /** @desc Token totals summed over every model call's `metadata.usage`. */
  export type TokenTotals = NonNullable<MessageMetadata['usage']> & { total: number };

  /**
   * @desc Per-conversation figures for the admin inspector, computed server-side from the event
   * log without shipping it. Tokens, not credits: credit weights live in the backend quota.
   *
   * - `turnCount` — distinct `metadata.turnId`s. `completedTurnCount` — turns that recorded a
   *   finish (`metadata.turn.tElapsed`); a turn still running, or one whose process died, has none.
   * - Turn durations are each completed turn's `tElapsed` (its largest, if several events carry
   *   one). `avgTurnElapsedMs`/`lastTurnElapsedMs` are null when no turn completed.
   * - `modelCalls` — model `ClaudeMessage`s, plus agent-sourced ones carrying usage (withheld
   *   drafts: a real model call the user never saw). `modelFailures` — `ModelCallFailed` events.
   * - `toolCalls` — `ToolOutput` events; `toolErrors` — those with a non-null `error`.
   * - `turnsWithErrors` — distinct turns containing a tool error or a model failure.
   */
  export type InspectorStats = {
    turnCount: number;
    completedTurnCount: number;
    totalTurnElapsedMs: number;
    avgTurnElapsedMs: number | null;
    lastTurnElapsedMs: number | null;
    modelCalls: number;
    modelFailures: number;
    toolCalls: number;
    toolErrors: number;
    turnsWithErrors: number;
    tokens: TokenTotals;
    artifacts: Artifact.FormatCounts;
  };

  /**
   * @desc An admin inspector conversation row: the metadata-only listing plus its stats, the
   * first prompt (truncated server-side — see {@link FirstPrompt}) and the wall-clock
   * {@link SessionSpan}. Both are null on a conversation with no user prompt.
   */
  export type InspectorListing = Listing & {
    stats: InspectorStats;
    firstPrompt: FirstPrompt | null;
    session: SessionSpan | null;
  };

  /**
   * @desc One user's usage across all their (non-deleted) conversations, for the inspector.
   *
   * - `activeDays` — distinct UTC days on which the user sent a prompt.
   * - `conversationsLast7Days`/`conversationsLast30Days` — conversations *created* in the
   *   trailing window, relative to the request time.
   * - `toolErrorRate` — `toolErrors / toolCalls`, null when no tool ran.
   * - `feedback` — the user's thumbs up/down verdict totals, all time.
   */
  export type InspectorUserStats = {
    userId: string;
    conversationCount: number;
    firstConversationAt: Date | null;
    lastActiveAt: Date | null;
    activeDays: number;
    conversationsLast7Days: number;
    conversationsLast30Days: number;
    totalTurns: number;
    completedTurns: number;
    avgTurnsPerConversation: number | null;
    avgTurnElapsedMs: number | null;
    turnsWithErrors: number;
    modelCalls: number;
    modelFailures: number;
    toolCalls: number;
    toolErrors: number;
    toolErrorRate: number | null;
    tokens: TokenTotals;
    modes: Record<AgentMode.T, number>;
    artifacts: Artifact.FormatCounts;
    feedback: { up: number; down: number };
  };
}
